import React, { useState, useEffect } from "react";
import { fetchUserTodos } from "../utils/todoUtils";
import {
  FaCheckCircle,
  FaCircle,
  FaSpinner,
  FaArchive,
  FaPlay,
  FaSync,
} from "react-icons/fa";
import { Auth } from "aws-amplify";

const TodoList = ({ todoCategory = "general" }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Authentication check failed:", err);
        setIsAuthenticated(false);
        setError("You must be signed in to view your todo list.");
      }
    };

    checkAuth();
  }, []);

  // Load todos when authenticated
  useEffect(() => {
    const loadTodos = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`Attempting to fetch todos for category: ${todoCategory}`);
        const fetchedTodos = await fetchUserTodos(todoCategory);

        console.log("Todos fetched successfully:", fetchedTodos);
        setTodos(fetchedTodos);
      } catch (err) {
        console.error("Error loading todos:", err);
        setError(
          `Failed to load your todo list: ${err.message || "Unknown error"}. Please try again later.`
        );
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadTodos();
    }
  }, [todoCategory, isAuthenticated, retryCount]);

  // Helper function to get the status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
        return <FaCheckCircle className="text-green-500" />;
      case "in progress":
        return <FaPlay className="text-blue-500" />;
      case "archived":
        return <FaArchive className="text-gray-500" />;
      case "not started":
      default:
        return <FaCircle className="text-gray-400" />;
    }
  };

  // Format the deadline date if it exists
  const formatDeadline = (deadline) => {
    if (!deadline) return "";

    try {
      const date = new Date(deadline);
      return date.toLocaleDateString();
    } catch (e) {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-blue-500 text-2xl" />
        <span className="ml-2 text-gray-300">Loading your todo list...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 bg-opacity-30 border border-red-800 rounded-lg p-4 text-center">
        <p className="text-red-300 mb-3">{error}</p>
        <button
          onClick={() => setRetryCount((prev) => prev + 1)}
          className="flex items-center justify-center mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          <FaSync className="mr-2" /> Retry
        </button>
      </div>
    );
  }

  if (todos.length === 0) {
    return (
      <div className="bg-gray-800 bg-opacity-50 border border-gray-700 rounded-lg p-6 text-center">
        <h3 className="text-xl font-medium text-gray-300 mb-2">
          No Todos Found
        </h3>
        <p className="text-gray-400">
          You don't have any todos in this category yet. Try asking the AI to
          create some tasks for you!
        </p>
      </div>
    );
  }

  const handleDebugClick = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      console.log("Current authenticated user:", user);

      // Extract user ID information
      const userId =
        user.attributes?.sub ||
        user.signInUserSession?.idToken?.payload?.sub ||
        user.username;

      console.log("User ID to be used for todos:", userId);
      console.log("Selected category:", todoCategory);

      // Trigger a retry
      setRetryCount((prev) => prev + 1);

      alert(`Debug info logged to console. User ID: ${userId}`);
    } catch (err) {
      console.error("Debug error:", err);
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="bg-gray-800 bg-opacity-50 border border-gray-700 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-blue-300">Your Todo List</h2>
        <button
          onClick={handleDebugClick}
          className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300"
          title="Log debug info to console"
        >
          Debug
        </button>
      </div>
      <div className="space-y-4">
        {todos.map((todo) => (
          <div
            key={todo.id}
            className="bg-gray-700 bg-opacity-50 border border-gray-600 rounded-lg p-4 hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-start">
              <div className="mt-1 mr-3">{getStatusIcon(todo.data.status)}</div>
              <div className="flex-1">
                <h3 className="font-medium text-white">{todo.data.task}</h3>

                {todo.data.time_to_complete && (
                  <p className="text-sm text-gray-300 mt-1">
                    <span className="font-medium">Estimated time:</span>{" "}
                    {todo.data.time_to_complete} minutes
                  </p>
                )}

                {todo.data.deadline && (
                  <p className="text-sm text-gray-300 mt-1">
                    <span className="font-medium">Deadline:</span>{" "}
                    {formatDeadline(todo.data.deadline)}
                  </p>
                )}

                {todo.data.solutions && todo.data.solutions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-300">
                      Solutions:
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-400 mt-1 ml-2">
                      {todo.data.solutions.map((solution, index) => (
                        <li key={index}>{solution}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-2">
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-gray-600 text-gray-300">
                    {todo.data.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TodoList;
